import {
    Appointment,
    RecurrenceInput,
    RecurringResult,
} from "../../model/appointment/Appointment";
import { AppointmentHistoryEvent } from "../../model/appointment/AppointmentHistoryEvent";
import { AppointmentInput } from "./AppointmentInput";
import i18n from "../../i18n";

const API_URL = process.env.REACT_APP_CALENDAR_SERVICE_BASE_URL || "";

const APPOINTMENT_FIELDS = `
    id
    businessId
    teamMemberId
    serviceId
    clientId
    customerName
    customerPhone
    customerEmail
    startTime
    endTime
    status
    paymentStatus
    extraDiscount
    price
    paymentType
    healthPlanId
    copayAmount
    holdPaymentIntentId
    holdAmount
    notes
    externalLocation
    cancellationReason
    cancelledByClient
    cancellationFeeOutstanding
    cancellationFeeAmount
    rescheduleStatus
    proposedStartTime
    proposedEndTime
    seriesId
    recurrenceFreq
    arrivedAt
    calledAt
    calledBy
    rebookReminderOn
`;

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors.map((e: { message: string }) => e.message).join(", ");
        throw new Error(msg);
    }
    return json.data as T;
}

export async function getAppointmentsByBusinessAndRange(
    businessId: string,
    start: string,
    end: string,
    token: string
): Promise<Appointment[]> {
    const data = await gqlPost<{ getAppointmentsByBusinessAndRange: Appointment[] | null }>(
        token,
        `query getAppointmentsByBusinessAndRange($businessId: ID!, $start: String!, $end: String!) {
            getAppointmentsByBusinessAndRange(businessId: $businessId, start: $start, end: $end) {
                ${APPOINTMENT_FIELDS}
            }
        }`,
        { businessId, start, end }
    );
    return data.getAppointmentsByBusinessAndRange ?? [];
}

/** Fetch a single appointment by id — used to refresh paymentStatus
 *  on the Calendar drawer after a payment-service sync, and by the
 *  Payments screen to enrich each row with appointment data. */
export async function getAppointmentById(
    id: string,
    token: string
): Promise<Appointment | null> {
    const data = await gqlPost<{ getAppointmentById: Appointment | null }>(
        token,
        `query getAppointmentById($id: ID!) {
            getAppointmentById(id: $id) {
                ${APPOINTMENT_FIELDS}
            }
        }`,
        { id }
    );
    return data.getAppointmentById ?? null;
}

/** Audit-log events for an appointment, newest first. */
export async function getAppointmentHistory(
    appointmentId: string,
    token: string
): Promise<AppointmentHistoryEvent[]> {
    const data = await gqlPost<{ getAppointmentHistory: AppointmentHistoryEvent[] | null }>(
        token,
        `query getAppointmentHistory($appointmentId: ID!) {
            getAppointmentHistory(appointmentId: $appointmentId) {
                id
                appointmentId
                eventType
                payload
                changedBy
                changedOn
            }
        }`,
        { appointmentId }
    );
    return data.getAppointmentHistory ?? [];
}

export async function getAppointmentsByClient(
    clientId: string,
    token: string
): Promise<Appointment[]> {
    const data = await gqlPost<{ getAppointmentsByClient: Appointment[] | null }>(
        token,
        `query getAppointmentsByClient($clientId: ID!) {
            getAppointmentsByClient(clientId: $clientId) {
                ${APPOINTMENT_FIELDS}
            }
        }`,
        { clientId }
    );
    return data.getAppointmentsByClient ?? [];
}

/** All occurrences of a recurring series, in chronological order. */
export async function getAppointmentsBySeries(
    seriesId: string,
    token: string
): Promise<Appointment[]> {
    const data = await gqlPost<{ getAppointmentsBySeries: Appointment[] | null }>(
        token,
        `query getAppointmentsBySeries($seriesId: ID!) {
            getAppointmentsBySeries(seriesId: $seriesId) {
                ${APPOINTMENT_FIELDS}
            }
        }`,
        { seriesId }
    );
    return data.getAppointmentsBySeries ?? [];
}

export async function getAppointmentsByTeamMember(
    teamMemberId: string,
    token: string
): Promise<Appointment[]> {
    const data = await gqlPost<{ getAppointmentsByTeamMember: Appointment[] | null }>(
        token,
        `query getAppointmentsByTeamMember($teamMemberId: ID!) {
            getAppointmentsByTeamMember(teamMemberId: $teamMemberId) {
                ${APPOINTMENT_FIELDS}
            }
        }`,
        { teamMemberId }
    );
    return data.getAppointmentsByTeamMember ?? [];
}

export async function saveAppointment(
    input: AppointmentInput,
    token: string
): Promise<Appointment> {
    const data = await gqlPost<{ saveAppointment: Appointment }>(
        token,
        `mutation saveAppointment($input: AppointmentInput!) {
            saveAppointment(input: $input) { ${APPOINTMENT_FIELDS} }
        }`,
        { input }
    );
    return data.saveAppointment;
}

/**
 * Book a recurring series. The backend expands the rule, validates each
 * occurrence against the team member's agenda, books the free ones (sharing a
 * seriesId) and returns the skipped dates.
 */
export async function createRecurringAppointment(
    input: AppointmentInput,
    recurrence: RecurrenceInput,
    token: string
): Promise<RecurringResult> {
    const data = await gqlPost<{ createRecurringAppointment: RecurringResult }>(
        token,
        `mutation createRecurringAppointment($input: AppointmentInput!, $recurrence: RecurrenceInput!) {
            createRecurringAppointment(input: $input, recurrence: $recurrence) {
                seriesId
                skipped
                created { ${APPOINTMENT_FIELDS} }
            }
        }`,
        { input, recurrence }
    );
    return data.createRecurringAppointment;
}

/** Accept (CONFIRM) or decline (CANCEL) a whole recurring series at once.
 *  `reason` is stored on the cancelled occurrences when declining. */
export async function respondSeries(
    seriesId: string,
    accept: boolean,
    token: string,
    reason?: string | null
): Promise<Appointment[]> {
    const data = await gqlPost<{ respondSeries: Appointment[] }>(
        token,
        `mutation respondSeries($seriesId: ID!, $accept: Boolean!, $reason: String) {
            respondSeries(seriesId: $seriesId, accept: $accept, reason: $reason) { ${APPOINTMENT_FIELDS} }
        }`,
        { seriesId, accept, reason: reason ?? null }
    );
    return data.respondSeries ?? [];
}

/** Hard-delete every occurrence of a recurring series. */
export async function deleteSeries(seriesId: string, token: string): Promise<boolean> {
    const data = await gqlPost<{ deleteSeries: boolean }>(
        token,
        `mutation deleteSeries($seriesId: ID!) { deleteSeries(seriesId: $seriesId) }`,
        { seriesId }
    );
    return !!data.deleteSeries;
}

export async function deleteAppointment(id: string, token: string): Promise<boolean> {
    const data = await gqlPost<{ deleteAppointment: boolean }>(
        token,
        `mutation deleteAppointment($id: ID!) { deleteAppointment(id: $id) }`,
        { id }
    );
    return !!data.deleteAppointment;
}

// ─── Waiting Room ────────────────────────────────────────────────────────────

/** Mark a client as arrived — sets arrivedAt and transitions status to WAITING. */
export async function checkInAppointment(
    token: string,
    appointmentId: string | number
): Promise<Appointment> {
    const data = await gqlPost<{ checkInAppointment: Appointment }>(
        token,
        `mutation checkInAppointment($id: ID!) {
            checkInAppointment(appointmentId: $id) {
                ${APPOINTMENT_FIELDS}
            }
        }`,
        { id: String(appointmentId) }
    );
    return data.checkInAppointment;
}

/** Call a waiting client to be seen — sets calledAt and transitions status to CALLED. */
export async function callPatient(
    token: string,
    appointmentId: string | number
): Promise<Appointment> {
    const data = await gqlPost<{ callPatient: Appointment }>(
        token,
        `mutation callPatient($id: ID!) {
            callPatient(appointmentId: $id) {
                ${APPOINTMENT_FIELDS}
            }
        }`,
        { id: String(appointmentId) }
    );
    return data.callPatient;
}

/** Fetch the live waiting room list for a business. */
export async function getMyWaitingRoom(
    token: string,
    businessId: string | number
): Promise<Appointment[]> {
    const data = await gqlPost<{ myWaitingRoom: Appointment[] | null }>(
        token,
        `query myWaitingRoom($businessId: ID!) {
            myWaitingRoom(businessId: $businessId) {
                ${APPOINTMENT_FIELDS}
            }
        }`,
        { businessId: String(businessId) }
    );
    return data.myWaitingRoom ?? [];
}

/** Today's scheduled patients who haven't arrived yet (the "A chegar" tab) —
 *  lets reception check them in from the waiting-room dropdown. */
export async function getMyExpectedToday(
    token: string,
    businessId: string | number
): Promise<Appointment[]> {
    const data = await gqlPost<{ myExpectedToday: Appointment[] | null }>(
        token,
        `query myExpectedToday($businessId: ID!) {
            myExpectedToday(businessId: $businessId) {
                ${APPOINTMENT_FIELDS}
            }
        }`,
        { businessId: String(businessId) }
    );
    return data.myExpectedToday ?? [];
}

// ─────────────────────────────────────────────────────────────────────────────

/** Suggest a new time to the customer. Flags the appointment PROPOSED until they respond. */
export async function proposeReschedule(
    appointmentId: string,
    proposedStartTime: string,
    proposedEndTime: string,
    token: string
): Promise<Appointment> {
    const data = await gqlPost<{ proposeReschedule: Appointment }>(
        token,
        `mutation proposeReschedule($appointmentId: ID!, $proposedStartTime: String!, $proposedEndTime: String!) {
            proposeReschedule(appointmentId: $appointmentId, proposedStartTime: $proposedStartTime, proposedEndTime: $proposedEndTime) {
                ${APPOINTMENT_FIELDS}
            }
        }`,
        { appointmentId, proposedStartTime, proposedEndTime }
    );
    return data.proposeReschedule;
}
